import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, MoreHorizontal, Plus, RefreshCw, ShieldAlert, Trash2, Users } from "lucide-react";

import {
  APIError,
  deleteAccessToken,
  listProjectMembers,
  listAccessTokens,
  removeProjectMember,
  rotateAccessToken,
  setProjectMember,
  type ProjectMember,
  type AccessToken,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { CopyButton, Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}

const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

// AdminOnly is the friendly stand-in shown when the viewer lacks admin on
// the project (the Access tab is admin-gated server-side).
function AdminOnly({ what }: { what: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
      <ShieldAlert className="h-6 w-6 opacity-50" />
      <span>
        You need <span className="font-medium text-foreground">admin</span> access on this project to{" "}
        {what}.
      </span>
    </div>
  );
}

const ROLE_CLS: Record<string, string> = {
  owner: "border-primary/30 bg-primary/10 text-primary",
  admin: "border-success/30 bg-success/10 text-success",
  editor: "border-warning/40 bg-warning/10 text-warning",
  viewer: "border-border bg-muted text-muted-foreground",
  member: "border-border bg-muted text-muted-foreground",
};

function RoleBadge({ role }: { role: string }) {
  const label = role === "member" ? "viewer" : role;
  return (
    <Badge variant="outline" className={cn("capitalize", ROLE_CLS[role] ?? ROLE_CLS.viewer)}>
      {label}
    </Badge>
  );
}

export function ProjectAccess({ projectId }: { projectId: string }) {
  return (
    <div className="space-y-6">
      <AccessTokensCard projectId={projectId} />
      <ProjectMembersCard projectId={projectId} />
    </div>
  );
}

// ── Access tokens ─────────────────────────────────────────────────────

function AccessTokensCard({ projectId }: { projectId: string }) {
  const [accounts, setAccounts] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<{ name: string; token: string } | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const goCreate = () => navigate(`/agents/${projectId}/access-tokens/new`);

  const refresh = useCallback(async () => {
    try {
      const list = await listAccessTokens(projectId);
      setAccounts(list);
      setErr(null);
      setForbidden(false);
    } catch (e) {
      if (is403(e)) setForbidden(true);
      else setErr(errMsg(e, "Couldn't load access tokens"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRotate = useCallback(
    async (sa: AccessToken) => {
      const ok = await confirm({
        title: `Rotate "${sa.name}"?`,
        description:
          "The current token stops working immediately. You'll get a new one to copy. Update any client using the old token.",
        confirmText: "Rotate",
        cancelText: "Cancel",
      });
      if (!ok) return;
      try {
        const res = await rotateAccessToken(projectId, sa.id);
        setNewToken({ name: res.access_token.name, token: res.token });
        await refresh();
      } catch (e) {
        toast.error(errMsg(e, "Rotate failed"));
      }
    },
    [projectId, confirm, toast, refresh],
  );

  const onDelete = useCallback(
    async (sa: AccessToken) => {
      const ok = await confirm({
        title: `Revoke "${sa.name}"?`,
        description: "Its token stops working immediately on the running deployment. This can't be undone.",
        confirmText: "Revoke",
        cancelText: "Cancel",
      });
      if (!ok) return;
      try {
        await deleteAccessToken(projectId, sa.id);
        setAccounts((cur) => cur.filter((x) => x.id !== sa.id));
        toast.success(`"${sa.name}" revoked.`);
      } catch (e) {
        toast.error(errMsg(e, "Revoke failed"));
      }
    },
    [projectId, confirm, toast],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Access tokens</CardTitle>
          <CardDescription>
            Named tokens for connecting machines to this project's <span className="font-mono">/mcp</span>. Rotate or
            revoke any without redeploying.
          </CardDescription>
        </div>
        {!forbidden && (
          <Button size="sm" onClick={goCreate}>
            <Plus className="h-4 w-4" /> New token
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : forbidden ? (
          <AdminOnly what="manage access tokens" />
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : accounts.length === 0 ? (
          <button
            onClick={goCreate}
            className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/30"
          >
            <KeyRound className="h-6 w-6 opacity-50" />
            <span>
              No access tokens yet. <span className="font-medium text-foreground">Create one</span> to give a
              machine scoped access.
            </span>
          </button>
        ) : (
          <ul className="divide-y rounded-md border">
            {accounts.map((sa) => (
              <li key={sa.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{sa.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {sa.prefix}…&nbsp;·&nbsp;created {new Date(sa.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Token actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => void onRotate(sa)}>
                      <RefreshCw /> Rotate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => void onDelete(sa)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 /> Revoke
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <TokenRevealDialog
        token={newToken}
        onClose={() => setNewToken(null)}
      />
    </Card>
  );
}

function TokenRevealDialog({
  token,
  onClose,
}: {
  token: { name: string; token: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={token !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy your token</DialogTitle>
          <DialogDescription>
            This is the only time <span className="font-medium text-foreground">{token?.name}</span>'s token is shown.
            Store it somewhere safe — you can't see it again.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs">
          <span className="truncate">{token?.token}</span>
          {token && <CopyButton value={token.token} />}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Project members ──────────────────────────────────────────────────────

const GRANT_OPTIONS = [
  { value: "", label: "Inherit (workspace role)" },
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
  { value: "admin", label: "Admin" },
];

function ProjectMembersCard({ projectId }: { projectId: string }) {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const toast = useToast();

  const refresh = useCallback(async () => {
    try {
      const list = await listProjectMembers(projectId);
      setMembers(list);
      setErr(null);
      setForbidden(false);
    } catch (e) {
      if (is403(e)) setForbidden(true);
      else setErr(errMsg(e, "Couldn't load members"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onChangeGrant = useCallback(
    async (m: ProjectMember, grant: string) => {
      setSaving(m.user_id);
      try {
        if (grant === "") {
          await removeProjectMember(projectId, m.user_id).catch((e) => {
            // A 404 here just means there was no grant to clear — treat as success.
            if (!(e instanceof APIError && e.status === 404)) throw e;
          });
          toast.success(`${m.email || m.user_id} reverted to their workspace role.`);
        } else {
          await setProjectMember(projectId, m.user_id, grant);
          toast.success(`${m.email || m.user_id} is now ${grant} on this project.`);
        }
        await refresh();
      } catch (e) {
        toast.error(errMsg(e, "Update failed"));
      } finally {
        setSaving(null);
      }
    },
    [projectId, toast, refresh],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Members</CardTitle>
        <CardDescription>
          Workspace members and their access on this project. A per-project grant elevates a member above their org
          role for this project only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : forbidden ? (
          <AdminOnly what="manage members" />
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground">
            <Users className="h-6 w-6 opacity-50" />
            <span>No workspace members.</span>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Workspace role</TableHead>
                <TableHead>Project grant</TableHead>
                <TableHead className="text-right">Effective</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const isOwner = m.org_role === "owner";
                return (
                  <TableRow key={m.user_id}>
                    <TableCell className="font-medium">{m.email || m.user_id}</TableCell>
                    <TableCell>
                      <RoleBadge role={m.org_role} />
                    </TableCell>
                    <TableCell>
                      {isOwner ? (
                        <span className="text-xs text-muted-foreground">— (owner)</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={m.project_grant ?? ""}
                            disabled={saving === m.user_id}
                            onChange={(e) => void onChangeGrant(m, e.target.value)}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                          >
                            {GRANT_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          {saving === m.user_id && <Spinner />}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <RoleBadge role={m.effective_role} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
